import type Rollbar from 'rollbar/src/browser/core'
import { inject, type InjectionKey } from 'vue'
import { isErrorPolyfill } from '@/lib/errors'

const isObject = (value: unknown): value is object =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

type Frame = {
  filename: string
  method?: string
  lineno: number | null
  colno: number | null
}

type PayloadBody = {
  telemetry?: Rollbar.TelemetryEvent[]
  trace?: {
    frames: Frame[]
  }
  trace_chain?: {
    frames: Frame[]
  }[]
}

export function rollbarTransform(payload: Rollbar.Payload) {
  if (!isObject(payload.body)) return
  const body = payload.body as PayloadBody
  for (const ev of body.telemetry ?? []) {
    if (ev.type !== 'network' || !ev.body) {
      continue
    }
    if ('url' in ev.body && typeof ev.body.url === 'string') {
      try {
        // strip sensitive query parameters from URLs - if we use `scrubFields` with 'key',
        // it over-scrubs other relevant 'key' fields
        const url = new URL(ev.body.url)
        if (url.searchParams.has('key')) {
          url.searchParams.set('key', '********')
        }
        ev.body.url = url.toString()
      } catch {
        // ignore errors - likely due to relative URLs without a proto/host
      }
    }
  }
}

export const rollbarKey = Symbol() as InjectionKey<Rollbar>

export function useRollbar(): Rollbar {
  if (import.meta.env.SSR) {
    // Return a mock Rollbar instance during SSR
    return {
      configure: () => {},

      log: () => {},
      debug: () => {},
      info: () => {},
      warn: () => {},
      error: () => {},
      critical: () => {},
    } as unknown as Rollbar
  }

  const rollbar = inject(rollbarKey)
  if (!rollbar) {
    throw new Error('Rollbar instance not found')
  }
  return rollbar
}

export function rollbarError(error: unknown): Error | string {
  if (isErrorPolyfill(error)) {
    return error
  }
  if (typeof error === 'string') {
    return error
  }
  return JSON.stringify(error)
}

export function rollbarArg(arg: unknown): Rollbar.LogArgument {
  return arg === null ? '<null>' : arg
}
